<template>
  <div
    v-if="isTokenValid && isTokenValid.data"
    class="main-container-w-background d-flex align-center justify-center"
  >
    <v-card
      :elevation="24"
      class="pa-5 login-card"
      v-if="
        isTokenValid.data.message != 'Invalid Session Error' &&
        isTokenValid.data.message != 'Session Expired'
      "
    >
      <div class="d-flex flex-column align-center justify-center">
        <!--
          ThreadFlow brand mark, matching /login's.

          PINNED (Unit 7, card https://trello.com/c/mq26K91V). This mark was
          previously unbound -- no data-testid and alt="" -- exactly the shape
          /login's mark had before auth-surface-threadflow-branding.spec.js
          bound it. It is the only product identification on the page, so it
          is not decorative: a screen-reader user should hear it, and its
          removal should be a RED test rather than a silent debrand.

          HOST-CONDITIONAL, for the same reason /login is. Jake said "the
          login page", but password reset is the surface that bites after the
          demo: a white-label user resetting a password lands here mid-flow
          and would meet ThreadFlow's brand in the middle of someone else's
          journey. That is the leak this unit exists to prevent, so the
          requirement's intent covers it.
        -->
        <img
          v-if="showBrand"
          src="@/assets/logo/logo.png"
          alt="ThreadFlow"
          data-testid="threadflow-brand-mark"
          style="max-height: 120px; max-width: 200px"
          class="mb-5 tf-logo no_select"
        />
        <v-col cols="12" md="6" sm="10" class="login">
          <v-form
            class="login-form"
            @submit.prevent="resetPassword"
            v-model="valid"
            ref="form"
          >
            <div class="d-flex flex-column align-center justify-center">
              <div style="width: 350px">
                <v-card-text class="ma-0 pa-0 mb-3">
                  <v-text-field
                    id="newPassword"
                    prepend-inner-icon="lock"
                    name="password"
                    type="password"
                    v-model="newPassword"
                    :rules="passwordRules"
                    placeholder="Reset Password"
                    data-testid="new-password"
                    variant="solo"
                    rounded
                  >
                  </v-text-field>
                  <v-text-field
                    id="confirmPassword"
                    prepend-inner-icon="lock"
                    name="password"
                    type="password"
                    v-model="confirmPassword"
                    :rules="passwordRules"
                    placeholder="Confirm Password"
                    data-testid="confirm-password"
                    variant="solo"
                    rounded
                  >
                  </v-text-field>
                  <!--
                    Only nag once the user has actually typed into both
                    fields. Keyed off `!=` between two empty strings, the
                    old condition was false at rest, but it fired the
                    moment the first character landed in either field --
                    telling the user the passwords do not match before
                    they had finished typing the first one.
                  -->
                  <div
                    v-if="showMismatchWarning"
                    class="no_select d-flex justify-center"
                    data-testid="password-mismatch"
                  >
                    New password and confirm password should be matched.
                  </div>
                </v-card-text>
                <div class="text-start">
                  <v-btn
                    block
                    color="primary"
                    type="submit"
                    :loading="loading"
                    :disabled="!canSubmit"
                    data-testid="submit"
                    class="btn-login text-white"
                    >Reset Password
                  </v-btn>
                </div>
              </div>
            </div>
          </v-form>
        </v-col>
      </div>
    </v-card>
    <div v-else class="d-flex align-center justify-center">
      <h1>{{ isTokenValid.data.message }}</h1>
    </div>
  </div>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import store from "@/store";
// Unit 7: which host is serving this page decides whether it is branded.
import { resolvePreAuthBranding } from "@/utils/preAuthBranding";
// The shared account-password policy (twin of api/src/password_policy.py).
import { passwordRules, validatePassword } from "@/utils/passwordPolicy";

class User {
  constructor(data) {
    this.account_id = data.account_id;
    this.token = data.token;
    this.token_type = data.token_type;
  }
}
export default {
  data() {
    return {
      // Unit 7: resolved once at creation -- see TheLoginPage for why this
      // is not a computed.
      showBrand: resolvePreAuthBranding().showBrand,
      newPassword: "",
      confirmPassword: "",
      valid: false,
      passwordRules,
      isTokenValid: null,
    };
  },

  computed: {
    loading() {
      return this.$store.getters.loading;
    },

    /**
     * Whether the form is submittable.
     *
     * Deliberately NOT derived from the <v-form> v-model (`valid`).
     * Under Vuetify 2 that model was a synchronous boolean that became
     * true once the rules passed; under Vuetify 3 it is
     * `true | false | null` -- null while validation is pending -- and
     * it does not eagerly resolve. Gating the button on it left the
     * button permanently disabled and the page unusable.
     *
     * The rules still run: they drive the per-field error messages, and
     * `resetPassword` awaits `validate()` before posting, so this
     * computed is a UI affordance rather than the validation itself.
     */
    canSubmit() {
      return (
        !this.loading &&
        validatePassword(this.newPassword) === true &&
        !!this.confirmPassword &&
        this.newPassword === this.confirmPassword
      );
    },

    showMismatchWarning() {
      return (
        !!this.newPassword &&
        !!this.confirmPassword &&
        this.newPassword !== this.confirmPassword
      );
    },
  },

  async mounted() {
    // if using custom shopper link
    if (this.$route.params.id) {
      this.isTokenValid = await this.$axios.get(
        `/auth/checkResetPasswordToken/${this.$route.params.id}`
      );
    }
  },

  methods: {
    async resetPassword() {
      // Vuetify 3: validate() is ASYNC and resolves to { valid, errors }.
      // The un-awaited call this replaces returned a Promise, which is
      // always truthy -- so the guard passed even when the form was
      // invalid. Await it and read `.valid` off the result.
      const { valid: isValid } = await this.$refs.form.validate();
      // Belt and braces with the field rules and the disabled button: Enter
      // in a field submits the form without going through the button.
      if (isValid && validatePassword(this.confirmPassword) === true) {
        const email = this.isTokenValid.data.message;
        let resp;
        try {
          resp = await this.$axios.post("/auth/resetPassword", {
            email: email,
            password: this.confirmPassword,
            reset_code: this.$route.params.id,
          });
        } catch (err) {
          // A refusal (for example the server's account-password policy)
          // has already been toasted by the response interceptor. Stay on
          // the form with the reset link still usable -- the server does
          // not consume it for a rejected password.
          return;
        }

        try {
          resp = await this.$axios.post("/auth/login", {
            login: email,
            password: this.confirmPassword,
          });
        } catch (err) {
          console.log(describeError(err));
          this.$store.dispatch("raiseError", err);
          this.forgotPassword = true;
          return;
        }
        // clear old account information in localStorage
        await this.$store.dispatch("logout");

        // save the token so we can make the next request to get the account's data
        const cleanToken = String(resp.data.token).replace(/^["']+|["']+$/g, '');
        localStorage.setItem("token", cleanToken);
        localStorage.setItem("userId", resp.data.account_id);
        
        // CRITICAL: Verify localStorage was actually written before proceeding
        const verifyToken = localStorage.getItem("token");
        if (verifyToken !== cleanToken) throw new Error('Token storage failed');
        
        const account = await this.$store.dispatch("jv/get", [
          `accounts/${resp.data.account_id}`,
          { params: { include: "role" } },
        ]);
        // save the new account information in localStorage.
        // GH #2941 defect 2: a completed password reset is a credentialed
        // sign-in, so clear any anonymous-storefront marker left on this
        // tab. See router/anonymousStorefrontSession.js.
        // authResponse hydrates the capability store: without it every
        // requireCapability guard on the landing route sees [] and bounces
        // the user to /login.
        this.$store.dispatch("login", {
          account,
          authResponse: resp.data,
          credentialed: true,
        });
        // ROUTING / post-auth landing page selection — NOT a permission gate.
        // Each role lands on its canonical home page after password reset; this
        // is a STORAGE/DISPATCH switch on the viewer's role enum, not an
        // authorization check (those are enforced by the destination route's
        // requireCapability guard). Migration of this dispatch to a
        // session-borne `home_path` is deferred to tier-2.
        // Route-fork PR-3 rename: "Distributor Manager" -> "Owner",
        // "Admin Buyer" -> "Client Admin".
        if (account.role.name == "Owner") {
          this.$router.push("/distributor/stores");
        } else if (account.role.name == "Sales Executive") {
          this.$router.push("/distributor/stores");
        } else if (account.role.name == "Shopper") {
          // reset VueX store
          this.$store.state.custom_checkout_fields = {};
          this.$router.push("/");
        } else if (account.role.name == "Client Admin") {
          this.$router.push("/admin/report");
        } else {
          this.$router.push("/admin");
        }
      }
    },
  },
};
</script>

<style scoped>
/*
 * Mirrors TheLoginPage.vue's card layout. The `.background`,
 * `.background-pos` and `.background-img` rules that used to live here
 * were removed with the full-viewport logo they styled -- that image
 * rendered many times the size of the form and behind it.
 */
.login {
  z-index: 100;
  display: flex;
  justify-content: center;
}

.btn-login {
  height: 48px !important;
  border-radius: 50px !important;
}

.login-form {
  width: 90%;
}

.main-container-w-background {
  height: 100vh;
  background-color: #f5f8fa;
}

.login-card {
  border-radius: 36px;
  padding: 50px 36px 50px 36px;
}

.tf-logo {
  image-rendering: crisp-edges;
}
</style>

